import React from 'react';
import { reduxForm } from 'redux-form';
import TextField from '@material-ui/core/TextField'
import { withStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import FormHelperText from '@material-ui/core/FormHelperText';
import MUIRichTextEditor from 'mui-rte';
import { stateToHTML } from 'draft-js-export-html';
import { convertToRaw, ContentState, convertFromHTML } from 'draft-js';

// Preserve underline/strikethrough across the HTML <-> Draft.js round-trip.
// draft-js-export-html defaults to <ins>/<del>, which draft-js convertFromHTML
// does not map back to UNDERLINE/STRIKETHROUGH inline styles.
const stateToHTMLOptions = {
    inlineStyles: {
        UNDERLINE: { element: 'u' },
        STRIKETHROUGH: { element: 's' }
    }
};

const buildRichTextInitialValue = (html) => {
    const source = html || '';
    const contentHTML = convertFromHTML(source);
    const contentState = contentHTML.contentBlocks
        ? ContentState.createFromBlockArray(contentHTML.contentBlocks, contentHTML.entityMap)
        : ContentState.createFromText('');
    return JSON.stringify(convertToRaw(contentState));
};

const styles = theme => ({
    form: {
        width: '100%',
        height: '100%',
    },
    footer: {
        margin: '20px 0px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
    },
    content: {
        marginTop: theme.spacing(3)
    },
    textVersion: {
        width: "100%"
    },
    richTextLabel: {
        marginTop: '10px',
        marginBottom: '4px',
        fontSize: '0.9rem',
        color: 'rgba(0, 0, 0, 0.6)'
    },
    richTextWrapper: {
        border: '1px solid #c4c4c4',
        borderRadius: '4px',
        padding: '0 10px',
        backgroundColor: '#ffffff',
        '& .DraftEditor-root': {
            minHeight: '300px',
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: '#ffffff'
        }
    },
    metaRow: {
        marginTop: theme.spacing(2),
        color: theme.palette.text.secondary,
        fontSize: '0.85rem'
    },
    btnSpacer: {
        marginRight: '12px'
    }
});

class AddTermsAndConditionsForm extends React.Component {
    constructor(props) {
        super(props);
        const initial = props.initialValues || {};
        this.state = {
            recordId: initial._id || null,
            isActive: initial.isActive === undefined ? 'No' : (initial.isActive ? 'Yes' : 'No'),
            htmlString: initial.htmlString || '',
            htmlStringInitialValue: buildRichTextInitialValue(initial.htmlString || ''),
            version: initial.version !== undefined && initial.version !== null ? String(initial.version) : '',
            privacyPolicy: initial.privacyPolicy || '',
            privacyPolicyInitialValue: buildRichTextInitialValue(initial.privacyPolicy || ''),
            editorKey: 0
        };
    }

    onSubmit = formValues => {
        if (!this.state.htmlString || !this.state.version) {
            return;
        }
        const payload = {
            ...formValues,
            isActive: this.state.isActive === 'Yes',
            htmlString: this.state.htmlString,
            version: this.state.version,
            privacyPolicy: this.state.privacyPolicy,
        };

        if (this.state.recordId && this.props.onUpdate) {
            this.props.onUpdate(this.state.recordId, payload);
        } else if (this.props.onCreate) {
            this.props.onCreate(payload);
        }
    }

    handleChange = (e) => {
        let value = e.target.value.trim();
        if (value) {
            this.setState({ [e.target.name]: e.target.value });
        } else {
            this.setState({ [e.target.name]: "" });
        }
    }

    handleHtmlStringChange = editorState => {
        const content = editorState.getCurrentContent();
        if (content.hasText()) {
            this.setState({
                htmlString: stateToHTML(content, stateToHTMLOptions)
            });
        } else {
            this.setState({ htmlString: '' });
        }
    }

    handlePrivacyPolicyChange = editorState => {
        const content = editorState.getCurrentContent();
        if (content.hasText()) {
            this.setState({
                privacyPolicy: stateToHTML(content, stateToHTMLOptions)
            });
        } else {
            this.setState({ privacyPolicy: '' });
        }
    }

    handleDelete = () => {
        if (!this.state.recordId) return;
        const confirmed = window.confirm('Are you sure you want to delete the saved Terms & Conditions and Privacy Policy? This cannot be undone.');
        if (!confirmed) return;
        if (this.props.onDelete) {
            this.props.onDelete(this.state.recordId);
        }
    }

    handleClear = () => {
        if (this.props.onClear) {
            this.props.onClear();
        }
    }

    render() {
        const { classes, editMode } = this.props;
        const { recordId, htmlString, htmlStringInitialValue, version, isActive, privacyPolicyInitialValue, editorKey } = this.state;
        const inEditMode = editMode && !!recordId;

        return (
            <form onSubmit={this.props.handleSubmit(this.onSubmit)} className={classes.form}>
                <Container className={classes.content}>
                    {inEditMode && (
                        <div className={classes.metaRow}>
                            Editing existing record (id: {recordId})
                        </div>
                    )}
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <FormControl variant="outlined" fullWidth className={classes.formControl} margin="normal">
                                <InputLabel htmlFor="t-and-c-is-active">Is Active</InputLabel>
                                <Select
                                    native
                                    value={isActive}
                                    onChange={this.handleChange}
                                    label="isActive"
                                    inputProps={{
                                        name: 'isActive',
                                        id: 't-and-c-is-active',
                                    }}
                                >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                name="version"
                                label="Version"
                                value={version}
                                variant="outlined"
                                margin="normal"
                                onChange={this.handleChange}
                                className={classes.textVersion}
                            />
                            {!version ? <FormHelperText error>Required</FormHelperText> : null}
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel className={classes.richTextLabel}>Terms &amp; Conditions</InputLabel>
                            <div className={classes.richTextWrapper}>
                                <MUIRichTextEditor
                                    key={`tc-${editorKey}`}
                                    label="Enter terms & conditions..."
                                    toolbarButtonSize="small"
                                    defaultValue={htmlStringInitialValue}
                                    value={htmlStringInitialValue}
                                    inlineToolbar={true}
                                    onChange={this.handleHtmlStringChange}
                                    controls={['title', 'bold', 'italic', 'underline', 'strikethrough', 'highlight', 'link', 'numberList', 'bulletList', 'quote', 'undo', 'redo']}
                                />
                            </div>
                            {!htmlString ? <FormHelperText error>Required</FormHelperText> : null}
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel className={classes.richTextLabel}>Privacy Policy</InputLabel>
                            <div className={classes.richTextWrapper}>
                                <MUIRichTextEditor
                                    key={editorKey}
                                    label="Enter privacy policy..."
                                    toolbarButtonSize="small"
                                    defaultValue={privacyPolicyInitialValue}
                                    value={privacyPolicyInitialValue}
                                    inlineToolbar={true}
                                    onChange={this.handlePrivacyPolicyChange}
                                    controls={['title', 'bold', 'italic', 'underline', 'strikethrough', 'highlight', 'link', 'numberList', 'bulletList', 'quote', 'undo', 'redo']}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Container>
                <Container className={classes.footer}>
                    {inEditMode ? (
                        <React.Fragment>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                className={classes.btnSpacer}
                            >
                                Update
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="secondary"
                                onClick={this.handleDelete}
                                className={classes.btnSpacer}
                            >
                                Delete
                            </Button>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={this.handleClear}
                            >
                                Clear
                            </Button>
                        </React.Fragment>
                    ) : (
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                        >
                            Submit
                        </Button>
                    )}
                </Container>
            </form>
        );
    }
}

const validate = values => {
    const errors = {}

    const requiredFields = [
        'htmlString',
        'version'
    ]

    requiredFields.forEach(field => {
        if (!values[field]) {
            errors[field] = 'Required'
        }
    })

    return errors
}

export default reduxForm({
    form: 'AddTermsAndConditionsForm',
    validate
})(withStyles(styles)(AddTermsAndConditionsForm))
